import React from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { merge, pathOr, propOr } from 'ramda';

import { Footer } from '@components/Footer';
import Header from '@components/HeaderContainer';
import LinkedResources from '@components/LinkedResources';
import SocialMedia, { HrefLang } from '@components/Meta';
import SEO from '@components/seo';
import MaybeData from '@contexts/MaybeData';
import { SearchSuggestionsProvider } from '@contexts/SearchSuggestions';
import AvailabilityStateProvider from '@fire/contexts/AvailabilityState';
import { GlobalStyles } from '@fire/globalStyles';
import withI18next from '@fire/i18n/withI18next';
import { HTMLText } from '@utils/HTMLText';
import { PageView } from '@utils/tracking';

import * as ß from './styles';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error(info.componentStack);
    console.error(error);
  }
  render() {
    return process.env.NODE_ENV === 'development' && this.state.hasError ? (
      <strong>
        Something is wrong with this page, please see logs for more details
      </strong>
    ) : (
      this.props.children
    );
  }
}
ErrorBoundary.propTypes = { children: PropTypes.node };

const StandardPage = props => {
  const pageContext = merge(
    propOr({}, 'pageContext', props),
    pathOr({}, ['data', 'currentPage', 'context'], props)
  );

  return (
    <>
      <MaybeData.Provider value={pageContext}>
        <AvailabilityStateProvider hasHash={false}>
          <SearchSuggestionsProvider>
            <GlobalStyles />
            <PageView />
            <SEO pageContext={pageContext} />
            <HrefLang />
            <LinkedResources pageContext={pageContext} />
            <Helmet>
              <title>{pageContext.title}</title>
            </Helmet>
            <SocialMedia pageContext={pageContext} />
            <ErrorBoundary>
              <Header pageContext={pageContext} />
              <div css={ß.container} id="page-container">
                <div css={ß.html}>
                  <h1 css={ß.header}>{pageContext.h1}</h1>
                  <HTMLText source={pageContext.body} />
                </div>
              </div>
              <Footer pageContext={pageContext} />
            </ErrorBoundary>
          </SearchSuggestionsProvider>
        </AvailabilityStateProvider>
      </MaybeData.Provider>
    </>
  );
};

export const query = graphql`
  query StandardPageData($prn: String!) {
    currentPage: campfirePage(context: { prn: { eq: $prn } }) {
      path
      component
      context {
        pageType
        graphCMSLocale
        breadcrumbs
        body
        excludeFromSitemap
        entity
        metaDescription
        metaRobots
        name
        h1
        seoText
        siteUrl
        title
        prn
        partners {
          clickoutUrl
          id
          logo {
            altText
            alt_text
            url
            title
            campsite
            copyright
            fileName
            handle
            isMain
            name
            position
          }
          trackingId
        }
      }
    }
    locize: allLocale(
      filter: {
        namespace: {
          in: [
            "pt-map-country"
            "pt-hero-menu"
            "pt-footer"
            "pt-premium-partners-footer"
            "pt-signup-newsletter"
            "pt-language-selector"
            "pt-review-banner"
            "pt-cookie-banner"
            "pt-search-box"
            "pt-suggestion-list"
            "pt-search-result-list-tile"
          ]
        }
      }
    ) {
      edges {
        node {
          data
          id
          language
          namespace
        }
      }
    }
  }
`;

export default withI18next()(StandardPage);
